import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';

import {
    QUERIES_ADMIN,
    QUERIES_REAL_STATE,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, HelperOptions } from 'components/atoms';
import { Table } from 'components/organisms';
import { onErrorMessage, onSuccessMessage } from 'helpers';
import {
    getAdministratorPersonTagIndex,
    removeAdministratorPersonTag,
} from 'services';
import { sweetAlert } from 'utils';

import { PersonTagListWrap } from './styles';
import Layout from '../../Layout';

import type { PersonTagRemoveMutationInterface } from './interfaces';

const PersonTagList = () => {
    const titleMessages = 'Tags Usuários!';
    const theme = useTheme();
    const navigate = useNavigate();
    const { getLinkWithSlug } = useRealState();

    const queryClient = useQueryClient();

    const { data, isLoading: isLoadingList } = useQuery(
        QUERIES_ADMIN.USER_TAG.base,
        () => getAdministratorPersonTagIndex()
    );

    const { mutate: removeRegister }: PersonTagRemoveMutationInterface =
        useMutation((id) => removeAdministratorPersonTag(String(id)), {
            mutationKey: QUERIES_ADMIN.USER_TAG.base,
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Registro removido com sucesso!'
                );

                queryClient.invalidateQueries(QUERIES_ADMIN.USER_TAG.base);
                queryClient.invalidateQueries(QUERIES_REAL_STATE.USER_TAG.list);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        });

    function onHandleNavigate(param: string) {
        navigate(param);
    }

    function newRegister() {
        onHandleNavigate(getLinkWithSlug(ROUTES_ADMIN.USER_TAG.FORM.fullPath));
    }

    function editRegister(param: string) {
        onHandleNavigate(
            getLinkWithSlug(`${ROUTES_ADMIN.USER_TAG.FORM.fullPath}/${param}`)
        );
    }

    function removeRegisterRequest(id: number) {
        sweetAlert
            .fire({
                title: 'Você tem certeza?',
                text: 'Se você confirmar este registro será removido e está ação não poderá ser revertida!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: theme.colors.primary,
                cancelButtonColor: theme.colors.danger,
                confirmButtonText: 'Sim, remover registro!',
                cancelButtonText: 'Não, cliquei errado!',
            })
            .then((result) => {
                if (result.isConfirmed) {
                    removeRegister(id);
                }
            });
    }

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Tags da Pessoa',
                    href: getLinkWithSlug(ROUTES_ADMIN.USER_TAG.fullPath),
                },
            ]}
            title="Tags da Pessoa"
            buttonTitle={
                <Button color="nonary" onClick={() => newRegister()}>
                    Novo Registro
                </Button>
            }
        >
            <PersonTagListWrap className="user-tags-template">
                <Table
                    isLoading={isLoadingList}
                    columns={[
                        {
                            dataField: 'id',
                            text: 'ID',
                            sort: true,
                            headerStyle: { width: '15%' },
                        },
                        {
                            dataField: 'nome',
                            text: 'Nome',
                            sort: true,
                            headerStyle: { width: '35%' },
                        },
                        {
                            dataField: 'slug',
                            text: 'Slug',
                            sort: true,
                            headerStyle: { width: '35%' },
                        },
                        {
                            dataField: 'id',
                            text: 'Opções',
                            formatter: (fieldValue) =>
                                HelperOptions({
                                    edit: true,
                                    editFunction: () =>
                                        editRegister(fieldValue),
                                    removeFunction: () =>
                                        removeRegisterRequest(fieldValue),
                                }),
                            headerStyle: { width: '10%' },
                        },
                    ]}
                    data={data}
                />
            </PersonTagListWrap>
        </Layout>
    );
};

export default PersonTagList;
